import { useState } from "react";
import { toast } from "sonner";
import { Mail, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { EMAIL, PHONE } from "@/data/content";

const TREATMENT_IMG = "/assets/sano-room-treatment.jpg";
const WORKSHOP_IMG = "/assets/sano-room-workshop.jpg";

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  rental_type: "treatment_room",
  preferred_dates: "",
  duration: "",
  profession: "",
  message: "",
};

const TYPE_LABEL = {
  treatment_room: "Treatment room",
  workshop_space: "Workshop / class space",
  host_event: "Host an event or workshop at Sano",
};

function buildMailto(form) {
  const subject = `Room rental inquiry — ${TYPE_LABEL[form.rental_type]} (${form.name || "visitor"})`;
  const lines = [
    `Hi Sano Acupuncture,`,
    ``,
    `I'd like to inquire about renting your ${TYPE_LABEL[form.rental_type].toLowerCase()}.`,
    ``,
    `Name: ${form.name}`,
    `Email: ${form.email}`,
    form.phone ? `Phone: ${form.phone}` : null,
    form.profession ? `Profession / Modality: ${form.profession}` : null,
    form.preferred_dates ? `Preferred dates: ${form.preferred_dates}` : null,
    form.duration ? `Duration / Frequency: ${form.duration}` : null,
    ``,
    form.message ? `${form.message}` : null,
    ``,
    `Thank you,`,
    `${form.name}`,
  ].filter(Boolean);
  return `mailto:${EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join("\n"))}`;
}

export default function RoomRental() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/room-rental", form);
      // Open the visitor's email client pre-filled and addressed to the studio
      window.location.href = buildMailto(form);
      toast.success("Inquiry saved. Opening your email app to finish sending…");
      setForm(INITIAL);
    } catch {
      toast.error("Something went wrong. Try again or email sanoacu@gmail.com");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="rental"
      className="py-28 lg:py-36 border-y"
      style={{ background: "var(--sano-bg-2)", borderColor: "var(--sano-border)" }}
      data-testid="rental-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16 items-start">
        <div className="lg:col-span-5">
          <div className="eyebrow mb-4">07 · Space Rental</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl leading-tight mb-6">
            A quiet room — for your work, your class, or your gathering.
          </h2>
          <p className="text-base leading-relaxed mb-8" style={{ color: "var(--sano-ink-2)" }}>
            Sano&apos;s studio in the Bronx is open to licensed practitioners,
            teachers, and community-builders.
          </p>
          <div className="space-y-5 mb-8">
            <div>
              <div className="eyebrow mb-2">Workshops · Classes · Private Events</div>
              <p className="text-base leading-relaxed" style={{ color: "var(--sano-ink-2)" }}>
                Rent the space for a workshop, class, or private event at{" "}
                <strong style={{ color: "var(--sano-ink)" }}>$100 / hour</strong>.
                Yoga, breathwork, sound bath, somatic circle, postpartum gathering,
                art night, birthday, wellness retreat — if it serves the body and
                the nervous system, we want to hear about it.
              </p>
            </div>
            <div>
              <div className="eyebrow mb-2">Practitioner Treatment Room</div>
              <p className="text-base leading-relaxed" style={{ color: "var(--sano-ink-2)" }}>
                Licensed practitioners can rent a treatment room at a{" "}
                <strong style={{ color: "var(--sano-ink)" }}>weekly or monthly rate</strong>{" "}
                — once a week, or multiple days a week. Tell us your ideal
                schedule and we&apos;ll match you to open days.
              </p>
            </div>
          </div>
          <img
            src={TREATMENT_IMG}
            alt="Sano treatment room with warming heat lamp and massage table"
            className="w-full max-h-[640px] object-contain rounded-sm"
            style={{ background: "var(--sano-bg-3)" }}
            data-testid="rental-image-treatment"
          />
          <img
            src={WORKSHOP_IMG}
            alt="Sano workshop space with sunlit windows"
            className="w-full h-56 object-cover rounded-sm mt-3"
            data-testid="rental-image-workshop"
          />
          <div className="grid grid-cols-2 gap-6 mt-8">
            <div className="border p-5" style={{ borderColor: "var(--sano-border)", background: "var(--sano-bg)" }}>
              <div className="eyebrow mb-2">Workshops / Events</div>
              <div className="font-serif-display text-2xl">$100 / hr</div>
            </div>
            <div className="border p-5" style={{ borderColor: "var(--sano-border)", background: "var(--sano-bg)" }}>
              <div className="eyebrow mb-2">Practitioner Room</div>
              <div className="font-serif-display text-2xl">Weekly · Monthly</div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-6 lg:col-start-7 space-y-5" data-testid="rental-form">
          <RentalToggle value={form.rental_type} onChange={(v) => setForm({ ...form, rental_type: v })} />

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Name *" value={form.name} onChange={update("name")} testid="rental-name" />
            <Field label="Email *" type="email" value={form.email} onChange={update("email")} testid="rental-email" />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Phone" value={form.phone} onChange={update("phone")} testid="rental-phone" />
            <Field label="Profession / Modality" value={form.profession} onChange={update("profession")} testid="rental-profession" />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Preferred dates" value={form.preferred_dates} onChange={update("preferred_dates")} testid="rental-dates" />
            <Field label="Duration / Frequency" value={form.duration} onChange={update("duration")} testid="rental-duration" />
          </div>
          <TextArea label="Tell us about your work" value={form.message} onChange={update("message")} testid="rental-message" />

          <div className="flex flex-wrap items-center gap-4 pt-2">
            <button
              type="submit"
              disabled={loading}
              className="px-7 py-3.5 rounded-full text-sm font-medium text-[#F9F6F0] disabled:opacity-50"
              style={{ background: "var(--sano-olive)" }}
              data-testid="rental-submit"
            >
              {loading ? "Sending…" : "Send inquiry"}
            </button>
            <a
              href={`mailto:${EMAIL}?subject=${encodeURIComponent("Room rental inquiry")}`}
              className="inline-flex items-center gap-2 text-sm hover:underline"
              style={{ color: "var(--sano-olive)" }}
              data-testid="rental-mailto"
            >
              <Mail size={14} strokeWidth={1.5} /> Email {EMAIL}
            </a>
            <a
              href={`sms:9292726066?body=${encodeURIComponent("Hi Sano — I'd like to ask about renting the space or hosting an event.")}`}
              className="inline-flex items-center gap-2 text-sm hover:underline"
              style={{ color: "var(--sano-olive)" }}
              data-testid="rental-text"
            >
              <MessageSquare size={14} strokeWidth={1.5} /> Or text {PHONE}
            </a>
          </div>
          <p className="text-xs" style={{ color: "var(--sano-ink-2)" }}>
            On submit, your email app will open with a pre-filled message addressed to {EMAIL}.
          </p>
        </form>
      </div>
    </section>
  );
}

function RentalToggle({ value, onChange }) {
  return (
    <div
      className="inline-flex flex-wrap p-1 border rounded-full gap-1"
      style={{ borderColor: "var(--sano-border)", background: "var(--sano-bg)" }}
    >
      {[
        { v: "treatment_room", l: "Treatment room" },
        { v: "workshop_space", l: "Workshop space" },
        { v: "host_event", l: "Host an event" },
      ].map((o) => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          className="px-5 py-2 text-sm rounded-full transition-all"
          style={{
            background: value === o.v ? "var(--sano-olive)" : "transparent",
            color: value === o.v ? "var(--sano-bg)" : "var(--sano-ink)",
          }}
          data-testid={`rental-toggle-${o.v}`}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}</span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        className="w-full px-4 py-3 border bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#3A5A40] text-sm"
        style={{ borderColor: "var(--sano-border)" }}
        data-testid={testid}
      />
    </label>
  );
}

function TextArea({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}</span>
      <textarea
        value={value}
        onChange={onChange}
        rows={4}
        className="w-full px-4 py-3 border bg-[#F9F6F0] focus:outline-none focus:ring-2 focus:ring-[#3A5A40] text-sm resize-none"
        style={{ borderColor: "var(--sano-border)" }}
        data-testid={testid}
      />
    </label>
  );
}
